import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthContext';
import { onboardAccount, patchOnboarding, createFirstProject } from '../../lib/api/accounts';
import type { ProjectPlan } from '../../../../shared/types/plans';
import type { OnboardingAnswers } from '../../../../shared/types/accounts';
import {
  loadDraft, saveDraft, clearDraft, floorPlanAtTeam,
  DEFAULT_ANSWERS, DRAFT_VERSION, type WizardStep, type WizardDraft,
} from './state';
import { recommendPlan } from '../../../../shared/lib/planRecommendation';
import { StepWelcome } from './StepWelcome';
import { StepCodebase } from './StepCodebase';
import { StepGithub } from './StepGithub';
import { StepFirstEpic } from './StepFirstEpic';

/**
 * UX-08: collapsed from 8 steps to 4. Stack/Scale/Needs/Plan (the "tell
 * us about your codebase" questions that all feed one plan
 * recommendation) are now one screen (`StepCodebase`); API keys moved
 * from their own step into an optional disclosure on the GitHub step.
 * See feature-specs/webportal-ux/UX-08-onboarding-4-steps.md.
 */
const STEPS = ['Welcome', 'Your codebase', 'Connect GitHub', 'First epic'];

export const OnboardPage: React.FC = () => {
  const { me, refreshMe } = useAuth();
  const navigate = useNavigate();
  const email = (me?.accounts[0] as { ownerEmail?: string } | undefined)?.ownerEmail
    ?? (me as unknown as { email?: string })?.email
    ?? '';

  const [step, setStep] = useState<WizardStep>(0);
  const [accountName, setAccountName] = useState('');
  const [answers, setAnswers] = useState<OnboardingAnswers>(DEFAULT_ANSWERS);
  // FORGE-262: starter is not sold — signup defaults to the lowest sellable plan (team).
  const [chosenPlan, setChosenPlan] = useState<ProjectPlan>('team');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [repoFullName, setRepoFullName] = useState<string | null>(null);
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [accountError, setAccountError] = useState('');

  // UX-08 requirement 4: "If BYOK was never opened, also send byok_skipped
  // once when leaving [Connect GitHub]." Tracked here (not local to
  // StepGithub) because it has to survive across the disclosure being
  // collapsed again, and the "once" part has to survive re-visiting the
  // step via Back.
  const byokOpenedRef = useRef(false);
  const byokSkipSentRef = useRef(false);

  // Load draft from localStorage on mount
  useEffect(() => {
    const draft = loadDraft();
    if (!draft) return;
    setStep(draft.step);
    setAccountName(draft.accountName);
    setAnswers(draft.answers);
    setChosenPlan(draft.chosenPlan);
    setAccountId(draft.accountId);
    setProjectId(draft.projectId);
  }, []);

  // Persist draft whenever key state changes
  useEffect(() => {
    const draft: WizardDraft = { v: DRAFT_VERSION, step, accountName, answers, chosenPlan, accountId, projectId };
    saveDraft(draft);
  }, [step, accountName, answers, chosenPlan, accountId, projectId]);

  const next = () => setStep((s) => Math.min(s + 1, 3) as WizardStep);
  // Requirement 1: once the account exists, never go back before step 2
  // (Connect GitHub) — otherwise Continue on "Your codebase" would create
  // a second account.
  const back = () => setStep((s) => Math.max(s - 1, accountId ? 2 : 0) as WizardStep);

  // Step 1 ("Your codebase") → create account (same call the old Plan
  // step's Continue made).
  const handlePlanConfirm = async () => {
    setCreatingAccount(true);
    setAccountError('');
    try {
      const result = await onboardAccount({
        accountName: accountName.trim(),
        plan: chosenPlan,
        answers,
      });
      setAccountId(result.account.id);
      // Create the first project right after account creation
      try {
        const proj = await createFirstProject(result.account.id, accountName.trim());
        setProjectId(proj.id);
      } catch {
        // Project creation failing is non-fatal — wizard continues
      }
      await refreshMe();
      next();
    } catch (err) {
      setAccountError((err as Error).message);
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleByokOpen = () => {
    byokOpenedRef.current = true;
  };

  // Embedded BYOK save (StepGithub's disclosure) — telemetry only, no
  // step change: BYOK is no longer its own wizard step.
  const handleByokSaved = async () => {
    byokOpenedRef.current = true;
    if (accountId) {
      await patchOnboarding(accountId, { step: 'byok_saved' }).catch(() => {});
    }
  };

  const sendByokSkippedIfUnopened = async () => {
    if (byokOpenedRef.current || byokSkipSentRef.current || !accountId) return;
    byokSkipSentRef.current = true;
    await patchOnboarding(accountId, { step: 'byok_skipped' }).catch(() => {});
  };

  const handleGithubDone = async (repo: string | null) => {
    setRepoFullName(repo);
    if (accountId) {
      await patchOnboarding(accountId, { step: 'repo_attached' }).catch(() => {});
    }
    await sendByokSkippedIfUnopened();
    next();
  };

  const handleSkip = async () => {
    if (step === 2) {
      if (accountId) {
        await patchOnboarding(accountId, { step: 'github_skipped' }).catch(() => {});
      }
      await sendByokSkippedIfUnopened();
      next();
      return;
    }
    if (step === 3) {
      clearDraft();
      if (accountId) {
        await patchOnboarding(accountId, { step: 'first_epic_skipped' }).catch(() => {});
        await patchOnboarding(accountId, { step: 'completed', completedAt: new Date().toISOString() }).catch(() => {});
      }
      navigate('/', { replace: true });
      return;
    }
    next();
  };

  // Update the live recommendation as answers change — auto-bump plan
  // upward, floored at team (FORGE-262: starter isn't sellable, so it
  // never sticks even if a stale draft or the default answers recommend it).
  useEffect(() => {
    const rec = recommendPlan(answers);
    setChosenPlan((cur) => {
      const RANKS: Record<string, number> = { team: 0, business: 1, enterprise: 2 };
      const recPlan = floorPlanAtTeam(rec.plan);
      const curPlan = floorPlanAtTeam(cur);
      const curRank = RANKS[curPlan] ?? 0;
      const recRank = RANKS[recPlan] ?? 0;
      return recRank > curRank ? recPlan : curPlan;
    });
  }, [answers]);

  return (
    <div
      data-testid="onboard-page"
      className="min-h-screen flex items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-4 py-8"
    >
      {/* Requirement 7: step 1 ("Your codebase") holds a lot more fields
          than the others, so it gets a wider container. */}
      <div className={`w-full ${step === 1 ? 'max-w-2xl' : 'max-w-xl'}`}>
        {/* Header */}
        <div className="text-center mb-6">
          <div
            aria-hidden
            className="inline-flex h-12 w-12 items-center justify-center rounded-xl
                       bg-gradient-to-br from-primary-400 to-primary-600 text-white
                       text-xl font-bold shadow-card mb-3"
          >
            ⚒
          </div>
          <h1 className="text-2xl font-semibold text-slate-900">Set up Forge</h1>
          <p className="text-sm text-slate-600 mt-1">Step {step + 1} of {STEPS.length} — {STEPS[step]}</p>
        </div>

        {/* Progress bar + labelled segments (requirement 6) */}
        <div className="mb-6">
          <div className="w-full bg-slate-200 rounded-full h-1.5 flex gap-0.5 overflow-hidden">
            {STEPS.map((label, i) => (
              <div
                key={label}
                className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
                  i <= step ? 'bg-primary-500' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
          <div className="mt-1.5 flex text-2xs text-slate-500">
            {STEPS.map((label, i) => (
              <span
                key={label}
                className={`flex-1 text-center truncate px-0.5 ${i === step ? 'text-primary-700 font-medium' : ''}`}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Step card */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-card p-6">
          <h2 className="text-base font-semibold text-slate-900 mb-4">{STEPS[step]}</h2>

          {step === 0 && (
            <StepWelcome
              email={email}
              accountName={accountName}
              onChange={setAccountName}
              onNext={next}
            />
          )}
          {step === 1 && (
            <StepCodebase
              answers={answers}
              onAnswersChange={setAnswers}
              chosenPlan={chosenPlan}
              onChoosePlan={setChosenPlan}
              onNext={handlePlanConfirm}
              onBack={back}
              loading={creatingAccount}
              error={accountError}
            />
          )}
          {step === 2 && (
            <StepGithub
              projectId={projectId}
              onDone={handleGithubDone}
              onSkip={handleSkip}
              onBack={back}
              onByokOpen={handleByokOpen}
              onByokSaved={handleByokSaved}
            />
          )}
          {step === 3 && (
            <StepFirstEpic
              projectId={projectId}
              repoFullName={repoFullName}
              accountId={accountId}
              onSkip={handleSkip}
              onBack={back}
            />
          )}
        </div>

        {/* Skip nag banner for step 2 (GitHub) */}
        {step === 2 && (
          <p className="text-xs text-center text-slate-500 mt-4">
            You can skip this step and complete it later from your project settings.
          </p>
        )}
      </div>
    </div>
  );
};

export default OnboardPage;
